import { Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

// Generic count-up — tweens a host element's text content from whatever it
// last showed to the new bound number, instead of snapping straight to it.
// Pulled out of stat-tile's own (inline) count-up logic so dynamic, re-
// created-per-refresh data (e.g. a *ngFor of gate cards, where there's no
// single long-lived component instance to hold "the previous value") can
// get the same treatment via a plain attribute: `<strong [appCountUp]="n">`.
// Non-numeric input is rendered as-is, unanimated — this is purely additive
// polish, never a replacement for real formatting/pipes.
@Directive({
  selector: '[appCountUp]',
})
export class CountUpDirective implements OnChanges, OnDestroy {
  @Input('appCountUp') value: number | string | null;

  private static readonly DURATION_MS = 600;
  private frame: number | null = null;
  private displayed = 0;
  private hasRendered = false;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    const target = this.parseNumeric(this.value);
    if (target === null) {
      this.cancel();
      this.el.nativeElement.textContent = this.value === null || this.value === undefined ? '' : this.value.toString();
      return;
    }
    const from = this.hasRendered ? this.displayed : 0;
    this.hasRendered = true;
    this.tween(from, target);
  }

  private parseNumeric(raw: number | string | null | undefined): number | null {
    if (raw === null || raw === undefined) {
      return null;
    }
    const n = typeof raw === 'number' ? raw : parseFloat(raw);
    return isNaN(n) ? null : n;
  }

  private tween(from: number, to: number): void {
    this.cancel();
    if (from === to) {
      this.render(to);
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / CountUpDirective.DURATION_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      this.render(from + (to - from) * eased);
      if (t < 1) {
        this.frame = requestAnimationFrame(step);
      } else {
        this.frame = null;
      }
    };
    this.frame = requestAnimationFrame(step);
  }

  private render(value: number): void {
    this.displayed = value;
    this.el.nativeElement.textContent = Math.round(value).toString();
  }

  private cancel(): void {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  ngOnDestroy(): void {
    this.cancel();
  }
}
